/**
 * DashboardOrdersTab — Orders list + acceptance flow (iter54 rebuild).
 *
 * Responsibilities:
 *   1. Render every order with the full operator-facing payload
 *      (customer name, phone, email, delivery address, items, add-ons,
 *      notes, total, payment method, payment status, order status).
 *   2. Highlight pending-confirmation orders with a gold pulse outline
 *      so the owner can't miss a new ticket.
 *   3. Drive the "Accept Order" / "Reject Order" CTAs (which fire the
 *      customer notification chain on the backend).
 *   4. Surface the "Enable Sound Alerts" banner so the bell hook can
 *      get past the browser's autoplay policy on first visit.
 *   5. Keep the existing post-acceptance status progression buttons
 *      (Start Preparing → Mark Ready → Out for Delivery → Delivered)
 *      so the rest of the kitchen flow is unchanged.
 */
import React from 'react';
import {
  ShoppingBag,
  MessageCircle,
  MessageSquare,
  Mail,
  DollarSign,
  ChefHat,
  Printer,
  Bell,
  Check,
  X as XIcon,
  Phone,
  MapPin,
  CreditCard,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CHANNEL_CONFIG = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', cls: 'bg-green-50 text-green-600 border-green-200' },
  sms:      { icon: MessageSquare, label: 'SMS',     cls: 'bg-blue-50 text-blue-600 border-blue-200' },
  email:    { icon: Mail,          label: 'Email',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const PENDING_STATUSES = new Set(['pending_restaurant_confirmation', 'pending']);

const DashboardOrdersTab = ({
  orders,
  statusColors,
  statusLabels = {},
  updateOrderStatus,
  acceptOrder,
  rejectOrder,
  bellEnabled,
  bellNeedsPrompt,
  enableBell,
}) => {
  const { t } = useTranslation();
  const pendingCount = orders.filter((o) => PENDING_STATUSES.has(o.status)).length;

  return (
    <div data-testid="dashboard-orders-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white">
          Orders
          {pendingCount > 0 && (
            <span
              data-testid="orders-pending-count"
              className="ml-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 align-middle"
            >
              <Bell className="w-3.5 h-3.5" />
              {pendingCount} pending
            </span>
          )}
        </h2>
      </div>

      {/* Enable Sound banner — visible until the operator clicks once so
          the Web Audio context can attach to a user gesture. */}
      {bellNeedsPrompt && (
        <div
          data-testid="enable-sound-banner"
          className="mb-4 flex items-start sm:items-center justify-between gap-3 flex-wrap p-4 rounded-2xl border border-gold-500/50 bg-gradient-to-r from-amber-500/15 to-amber-500/5"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 w-8 h-8 rounded-full bg-gold-500/20 text-gold-300 flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4" />
            </span>
            <div className="text-sm">
              <p className="font-bold text-white">Enable sound alerts for new orders</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Your browser blocks sound until you allow it once. We&apos;ll ring a short bell when a new order arrives.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={enableBell}
            data-testid="enable-sound-btn"
            className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl text-sm transition-colors"
          >
            Enable Sound
          </button>
        </div>
      )}
      {bellEnabled && !bellNeedsPrompt && (
        <div className="mb-4 text-xs text-emerald-300 flex items-center gap-1.5" data-testid="bell-enabled-indicator">
          <Bell className="w-3.5 h-3.5" /> Sound alerts active
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t('dashboard.no_orders')}</p>
        </div>
      ) : orders.map((order) => {
        const channels = Array.isArray(order.notify_channels) && order.notify_channels.length
          ? order.notify_channels
          : ['whatsapp'];
        const tip = Number(order.tip_amount || 0);
        const subtotal = Number(order.subtotal || 0);
        const deliveryFee = Number(order.delivery_fee || 0);
        const isPending = PENDING_STATUSES.has(order.status);
        const isDelivery = order.order_type === 'delivery';
        const missingAddress = isDelivery && !(order.delivery_address || '').trim();
        const statusLabel = statusLabels[order.status] || order.status;
        const paymentMethod = (order.payment_method || 'cash').replace(/_/g, ' ');
        const paymentStatus = (order.payment_status || 'pending').toLowerCase();
        const paymentStatusCls = paymentStatus === 'paid'
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-slate-100 text-slate-600';

        return (
          <div
            key={order.id}
            className={`bg-white rounded-2xl border p-6 mb-4 transition-all ${
              isPending
                ? 'border-amber-400 ring-2 ring-amber-300/60 shadow-[0_0_0_4px_rgba(245,158,11,0.08)]'
                : 'border-slate-200'
            }`}
            data-testid={`order-card-${order.id}`}
          >
            <div className="flex items-start justify-between mb-4 gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900">{order.order_number}</span>
                  <span
                    data-testid={`order-status-${order.id}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-slate-100 text-slate-600'}`}
                  >
                    {statusLabel}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                    {order.order_type}
                  </span>
                </div>
                <div className="text-sm text-slate-700 font-medium mt-2">{order.customer_name}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                  <Phone className="w-3 h-3" />
                  <a href={`tel:${order.customer_phone}`} className="hover:text-gold-600">
                    {order.customer_phone}
                  </a>
                </div>
                {order.customer_email && (
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" />
                    <a href={`mailto:${order.customer_email}`} className="hover:text-gold-600 truncate max-w-[260px]">
                      {order.customer_email}
                    </a>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`order-channels-${order.id}`}>
                  <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mr-1">Contact via:</span>
                  {channels.map((c) => {
                    const cfg = CHANNEL_CONFIG[c];
                    if (!cfg) return null;
                    const Icon = cfg.icon;
                    return (
                      <span
                        key={c}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.cls}`}
                        data-testid={`order-channel-${c}-${order.id}`}
                      >
                        <Icon className="w-3 h-3" /> {cfg.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-gold-600">${Number(order.total || 0).toFixed(2)}</div>
                {tip > 0 && (
                  <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1" data-testid={`order-tip-${order.id}`}>
                    <DollarSign className="w-3 h-3 text-emerald-600" />
                    <span>Tip <span className="text-emerald-700 font-semibold">${tip.toFixed(2)}</span></span>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery address — prominent + flagged red when missing. */}
            {isDelivery && (
              <div
                className={`mb-3 flex items-start gap-2 text-sm px-3 py-2.5 rounded-xl border ${
                  missingAddress
                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
                data-testid={`order-delivery-address-${order.id}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="leading-tight">
                  <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-0.5">Delivery Address</p>
                  <p>
                    {missingAddress
                      ? 'Missing — do not dispatch this order.'
                      : order.delivery_address}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              {order.items.map((item, i) => {
                const mods = Array.isArray(item.modifiers) ? item.modifiers : [];
                const itemNotes = (item.notes || '').trim();
                const modDelta = mods.reduce(
                  (s, m) => s + Number(m.price_delta || 0) * Math.max(1, Number(m.quantity || 1)),
                  0,
                );
                const lineSubtotal = (Number(item.price) + modDelta) * Number(item.quantity);
                return (
                  <div key={i} className="py-1.5" data-testid={`order-item-${order.id}-${i}`}>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-800 font-medium">{item.quantity}x {item.name}</span>
                      <span className="text-slate-500 tabular-nums">${lineSubtotal.toFixed(2)}</span>
                    </div>
                    {mods.length > 0 && (
                      <ul
                        className="ml-5 mt-1 space-y-0.5 text-xs text-slate-600"
                        data-testid={`order-item-${order.id}-${i}-mods`}
                      >
                        {mods.map((m, mi) => {
                          const q = Math.max(1, Number(m.quantity || 1));
                          const modSubtotal = Number(m.price_delta || 0) * q;
                          return (
                            <li key={mi} className="flex justify-between gap-2">
                              <span className="truncate">
                                <span className="font-medium text-slate-700">
                                  {q > 1 ? `${q}× ` : ''}{m.option}
                                </span>
                              </span>
                              {modSubtotal > 0 && (
                                <span className="text-slate-500 tabular-nums shrink-0">+${modSubtotal.toFixed(2)}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {itemNotes && (
                      <p
                        className="ml-5 mt-1 text-xs italic text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                        data-testid={`order-item-${order.id}-${i}-notes`}
                      >
                        Note: {itemNotes}
                      </p>
                    )}
                  </div>
                );
              })}
              {(tip > 0 || deliveryFee > 0) && (
                <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5">
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Delivery fee</span><span>${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  {tip > 0 && (
                    <div className="flex justify-between text-xs text-emerald-700 font-semibold">
                      <span>Tip (100% to staff)</span><span>${tip.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Order-level notes (kitchen instructions). */}
            {order.notes && (
              <div
                className="mb-3 text-xs italic text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                data-testid={`order-notes-${order.id}`}
              >
                <span className="font-semibold not-italic mr-1">Order notes:</span>{order.notes}
              </div>
            )}

            {/* Payment summary row. */}
            <div className="mb-3 flex items-center gap-2 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                <CreditCard className="w-3 h-3" /> <span className="capitalize">{paymentMethod}</span>
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${paymentStatusCls}`}
                data-testid={`order-payment-status-${order.id}`}
              >
                {paymentStatus}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 items-center mt-3">
              {/* ── Acceptance step — Accept / Reject ────────────────── */}
              {isPending && (
                <>
                  <button
                    onClick={() => acceptOrder(order.id)}
                    disabled={missingAddress}
                    title={missingAddress ? 'Delivery address is missing on this order.' : undefined}
                    data-testid={`order-accept-${order.id}`}
                    className="px-3.5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors inline-flex items-center gap-1.5"
                  >
                    <Check className="w-4 h-4" /> Accept Order
                  </button>
                  <button
                    onClick={() => rejectOrder(order.id)}
                    data-testid={`order-reject-${order.id}`}
                    className="px-3 py-2 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5"
                  >
                    <XIcon className="w-4 h-4" /> Reject
                  </button>
                </>
              )}

              {/* Kitchen-flow CTAs after acceptance (kept from existing impl). */}
              {(order.status === 'accepted_by_restaurant' || order.status === 'confirmed') && (
                <button onClick={() => updateOrderStatus(order.id, 'preparing')} className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors">Start Preparing</button>
              )}
              {order.status === 'preparing' && <button onClick={() => updateOrderStatus(order.id, 'ready')} className="px-3 py-1.5 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors">Mark Ready</button>}
              {order.status === 'ready' && <button onClick={() => updateOrderStatus(order.id, 'out_for_delivery')} className="px-3 py-1.5 bg-indigo-500 text-white text-sm rounded-lg hover:bg-indigo-600 transition-colors">Out for Delivery</button>}
              {order.status === 'out_for_delivery' && <button onClick={() => updateOrderStatus(order.id, 'delivered')} className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors">Mark Delivered</button>}

              <div className="ml-auto flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    window.open(`/orders/${order.id}/kitchen`, '_blank', 'noopener,noreferrer');
                    window.open(`/orders/${order.id}/receipt`, '_blank', 'noopener,noreferrer');
                  }}
                  data-testid={`order-print-both-${order.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-black bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 rounded-lg transition-colors shadow-[0_4px_10px_-4px_rgba(212,175,55,0.5)]"
                  title="Open both kitchen ticket and customer receipt"
                >
                  <Printer className="w-3.5 h-3.5" /> Print Both
                </button>
                <a
                  href={`/orders/${order.id}/kitchen`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`order-print-kitchen-${order.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                  title="Open kitchen ticket"
                >
                  <ChefHat className="w-3.5 h-3.5" /> Kitchen
                </a>
                <a
                  href={`/orders/${order.id}/receipt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`order-print-receipt-${order.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
                  title="Open customer receipt"
                >
                  <Printer className="w-3.5 h-3.5" /> Receipt
                </a>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DashboardOrdersTab;
